import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface AuthCheckProps {
  children: React.ReactNode;
}

const PUBLIC_ROUTE_PREFIXES = [
  '/auth',
  '/u/',
  '/healthz',
  '/community-guidelines',
  '/terms-of-service',
  '/privacy-policy'
];

// Routes that are public AND show different content for logged-out users
const LANDING_ROUTES = ['/'];

export function AuthCheck({ children }: AuthCheckProps) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isPublicRoute = PUBLIC_ROUTE_PREFIXES.some((route) =>
    location.pathname.startsWith(route)
  );
  
  // Landing routes show different content for logged-out users (not redirect to /auth)
  const isLandingRoute = LANDING_ROUTES.includes(location.pathname);

  // Redirect unauthenticated users away from private routes only.
  // All onboarding redirection is now handled inside the onboarding pages themselves
  // to avoid global redirect loops and history.replaceState errors.
  useEffect(() => {
    // Don't redirect for public routes or landing routes (they handle their own auth state)
    if (!authLoading && !user && !isPublicRoute && !isLandingRoute) {
      navigate('/auth', { replace: true });
    }
  }, [authLoading, user, isPublicRoute, isLandingRoute, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // While redirecting to /auth, render nothing to avoid flicker
  // But allow landing routes to render (they show landing page for logged-out users)
  if (!user && !isPublicRoute && !isLandingRoute) {
    return null;
  }

  return <>{children}</>;
}
